import { Filter } from '@bingads-webui/listener';
import { CURRENT_URL_MARKER, GLOBAL_ERROR_APIS, REPEAT_COUNT_MARKER } from '../utilities/error-message-markers';
import type { InstrumentationLog } from '../types';

const MAX_TRACKED_SIGNATURE_CHARACTERS = 80000;
const VERBATIM_LIMIT = 20;
const SAMPLE_INTERVAL = 50;
const MAX_TRACKED_SIGNATURES = 200;

/**
 * Reduces a global error report to the fault description alone.
 *
 * The `CurrentURL:` tail is dropped because it changes on every route: keeping it would make one
 * fault look like a distinct fault per page, and would pull the account query parameters into the key.
 * The full remaining identity is compared; the filter bounds total retained characters instead of
 * truncating identities that could differ only in their source location.
 * @param {*} message - the raw error message ErrorStub produced
 * @returns {string|undefined} the signature, or undefined when the message is not a parseable report
 */
function getSignature(message: InstrumentationLog['message']): string | undefined {
  if (typeof message !== 'string') {
    return undefined;
  }

  const tailIndex = message.lastIndexOf(CURRENT_URL_MARKER);
  const identity = (tailIndex === -1 ? message : message.substring(0, tailIndex)).trim();

  return identity || undefined;
}

/**
 * Caps repeated identical global error reports within a single document.
 *
 * `window.onerror` and `onunhandledrejection` are reported with no throttle, so a fault that repeats
 * on a timer emits one row per occurrence for the life of the document - one client produced 35,942
 * rows of a single signature across 20 routes in two days, which buried every other signal on the
 * client error dashboards.
 *
 * One instance is created per page load, so the counters are scoped to one document. Distinct faults
 * never suppress each other, and anything that cannot be positively identified as a repeat is kept.
 *
 * Every report that survives sampling carries a `RepeatCount`, so a query can recover the true
 * occurrence rate rather than reading the reduced row count as a recovery. Writing that onto the log
 * is why this filter, unlike its siblings, touches the value it is passed.
 */
export class ErrorStormFilter extends Filter {
  counts: Map<string, number>;

  private trackedSignatureCharacters = 0;

  constructor() {
    super();
    this.counts = new Map();
  }

  /**
   * @param {InstrumentationLog} log - the log being considered
   * @returns {boolean} true to drop the log as a redundant repeat, false to keep it
   */
  check(log: InstrumentationLog): boolean {
    try {
      if (!log || GLOBAL_ERROR_APIS.indexOf(log.api as string) === -1) {
        return false;
      }

      const signature = getSignature(log.message);
      if (signature === undefined) {
        return false;
      }

      const seen = this.counts.get(signature);
      if (seen === undefined) {
        if (this.counts.size >= MAX_TRACKED_SIGNATURES
          || this.trackedSignatureCharacters + signature.length > MAX_TRACKED_SIGNATURE_CHARACTERS) {
          return false;
        }
        this.trackedSignatureCharacters += signature.length;
      }

      const count = (seen || 0) + 1;
      this.counts.set(signature, count);

      if (count <= VERBATIM_LIMIT) {
        return false;
      }

      if (count % SAMPLE_INTERVAL !== 0) {
        return true;
      }

      // eslint-disable-next-line no-param-reassign
      log.message = `${log.message}${REPEAT_COUNT_MARKER}${count}`;
      return false;
    } catch (e) {
      return false;
    }
  }
}
